import DestinationPicker from "../components/destinationPicker.jsx";
import DeparturePicker from "../components/departurePicker.jsx";
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import DatePicker from "../components/datePicker.jsx";
import PaxPicker from "../components/paxPicker.jsx";
import PackageDatasource from "../datasources/packageDatasource.js";
import IslandHopPicker from "../components/islandHopPicker.jsx";
import useWindowDimensions from "../helpers/Hooks/useWindowDimensions";
import useJsonCookie from "../helpers/Hooks/useJsonCookie.jsx";
import { format, parse, startOfTomorrow } from "date-fns";
import { DEFAULT_ROOMS, serializeRooms, deserializeRooms, toSingleParty } from "../helpers/rooms.js";
import { computeStartWindow, buildBookingUrl } from "../helpers/bookingUrl.js";

import { IsMobileContext, DataSourceContext, FormPanelContext } from '../context/contexts.jsx'
import { SunIcon, BedIcon, SignpostIcon, HotelIcon } from "../../../SubModules/Olympic.DesignFramework/react/icons.jsx";
// Tab icons: the designer's icons (Olympic.DesignFramework assets/icons), in currentColor so they follow the tab text.
const tabIconClass = "h-7 w-auto md:h-5";
const sunIcon = <SunIcon className={tabIconClass} />;
const bedIcon = <BedIcon className={tabIconClass} />;
const signpostIcon = <SignpostIcon className={tabIconClass} />;
const villaIcon = <HotelIcon className={tabIconClass} />;
const SEARCH_TYPES = [
    { value: "packages", label: "Holidays", icon: sunIcon },
    { value: "stays", label: "Hotel Only", shortLabel: "Hotels", icon: bedIcon },
];
// The Villas tab (the category comp's last tab) covers two search types, chosen with a switch on the card: Villa is Hotel
// Only narrowed to villas (no departure airport), Villa + Flights is Holidays narrowed to villas (Almanac docs/29). Both
// take one party - guests, no rooms - as a villa is one booking.
const VILLA_TAB = { value: "villas", label: "Villas", icon: villaIcon };
const VILLA_TYPES = [
    { value: "villa-flights", label: "Villa + Flights" },
    { value: "villas", label: "Villa only" },
];
const isVillaType = (type) => type == "villas" || type == "villa-flights";
// Island hopping has no booking: its tab is a list of the island hop pages
// (from Umbraco, via data-island-hops) and the button goes to the chosen page.
const ISLAND_TAB = { value: "island", label: "Island Hopping", icon: signpostIcon };
// Stable sentinel passed as departureAirports to the pickers when in "stays"
// mode - hotel only searches have no departure airports, but the pickers
// enable themselves (and re-fetch) based on that prop. The hotel only
// datasource ignores the value.
const STAY_ONLY_DEPS = ["stay-only"];
const SearchOverview = ({ departures, destination, datesText, openSearch, isMobile }) => {
    return <div className="">
        {isMobile && (
            <button onClick={() => openSearch()} type="button"
                className="btn-primary text-white p-2 inline-flex items-center justify-center rounded-full bg-emp">
                <span className="h-[2rem] flex items-center justify-center px-4">Edit Search</span>
                <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.8043 11.25L12.9293 12.375M13.2543 14.2689C12.821 13.8356 12.821 13.1332 13.2543 12.7C13.6875 12.2667 14.3899 12.2667 14.8232 12.7L16.3543 14.2311C16.7876 14.6644 16.7876 15.3668 16.3543 15.8C15.9211 16.2333 15.2187 16.2333 14.7854 15.8L13.2543 14.2689ZM12.9293 7.125C12.9293 4.22551 10.5788 1.875 7.67932 1.875C4.77983 1.875 2.42932 4.22551 2.42932 7.125C2.42932 10.0245 4.77983 12.375 7.67932 12.375C10.5788 12.375 12.9293 10.0245 12.9293 7.125Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
            </button>
        )}
    </div>;
}

// variant "hero": over a banner image (white tab text, the fields on a 90% white card, frosted on mobile).
// variant "standalone": the pale blue band above a page's banner (the category comp, 7 Oct 2026) - the same card and
// layout as the hero, with ink tabs and a solid white card with no shadow.
// islandHopping: offer the Island Hopping tab (the website mounts; its hops load from the Search API).
const OlympicSearch = ({ overviewNode, specificSearch, specificAccom, baseUrl, bookingEngineBaseUrl, showForm, islandHopping = false, variant = "standalone" }) => {
    if (specificSearch && specificAccom) {
        throw new Error("cannot do both");
    }

    const [savedRooms, setSavedRooms, removeSavedRooms] = useJsonCookie('s_rooms', DEFAULT_ROOMS, baseUrl);
    const [savedDeparture, setSavedDeparture, removeSavedDeparture] = useJsonCookie("s_dep", null, baseUrl);
    const [savedDestination, setSavedDestination, removeSavedDestination] = useJsonCookie("s_dest", null, baseUrl);
    const [savedStartDate, setSavedStartDate, removeStartDate] = useJsonCookie("s_from_date", null, baseUrl);
    const [savedToDate, setSavedToDate, removeToDate] = useJsonCookie("s_to_date", null, baseUrl);
    const [savedSearchType, setSavedSearchType] = useJsonCookie("s_type", "packages", baseUrl);
    const [savedDateMode, setSavedDateMode] = useJsonCookie("s_date_mode", "dates", baseUrl);
    const [savedDuration, setSavedDuration] = useJsonCookie("s_duration", null, baseUrl);
    const [savedFlexDays, setSavedFlexDays] = useJsonCookie("s_flex", 0, baseUrl);

    const [searchType, setSearchType] = useState(specificSearch ? (specificSearch.type ?? "packages") : (savedSearchType ?? "packages"));
    const [rooms, setRooms] = useState(savedRooms);
    // the Villas tab's two types; "stay-like" types have no departure airport
    const isVilla = isVillaType(searchType);
    const isStayLike = searchType == "stays" || searchType == "villas";
    // the last villa type picked, so the Villas tab reopens on it (Villa + Flights at first, like Holidays)
    const [villaType, setVillaType] = useState(isVilla ? searchType : "villa-flights");
    const [departures, setDepartures] = useState([]);
    // Multiple destinations can be selected; their codes are hyphen-joined into
    // the destination URL segment. Stored as an array of { name, code }.
    const [destinations, setDestinations] = useState([]);
    const [selectedFromDate, setSelectedFromDate] = useState(null);
    const [selectedToDate, setSelectedToDate] = useState(null);
    // "dates" = pick exact departure/return days; "month" = search the whole
    // month as a departure window with an explicit duration.
    const [dateMode, setDateMode] = useState(specificSearch ? "dates" : (savedDateMode ?? "dates"));
    const [selectedDuration, setSelectedDuration] = useState(null);
    // Fuzzy ± flexibility (in days) applied to the departure date in "dates"
    // mode; widens the earliest/latest start window in the booking URL.
    const [flexDays, setFlexDays] = useState(specificSearch ? 0 : (savedFlexDays ?? 0));
    const [overViewDepartureText, setOverViewDepartureText] = useState();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    // Per-field validation messages, populated only on a failed submit so we
    // don't nag before the user has tried to search. Keys: departure,
    // destination, dates.
    const [errors, setErrors] = useState({});
    const hasErrors = Object.values(errors).some(Boolean);
    const { height, width } = useWindowDimensions();
    // the mockup's breakpoint (md): one row of fields from 768px, stacked with full-screen panels below
    const isMobile = width < 768;
    // Memoise so the datasource (and the context value below) keeps a stable
    // identity between renders - recreating it every render made every picker
    // re-render needlessly.
    const dataSource = useMemo(() => PackageDatasource.getDataSource(searchType, baseUrl), [searchType, baseUrl]);
    // Departure airports only exist for packages, and the departure picker
    // fetches its list once on mount (it stays mounted-but-hidden in stays
    // mode). If the widget mounts on the Hotel Only tab (saved s_type cookie)
    // the stays datasource would seed that one fetch with an empty list, and
    // flipping to Holidays then shows "No departure airports available" - so
    // the picker always gets the packages source.
    const packagesDataSource = useMemo(() => PackageDatasource.getDataSource("packages", baseUrl), [baseUrl]);
    // In stays mode the pickers receive a stable sentinel instead of real
    // departure airports (see STAY_ONLY_DEPS above).
    const effectiveDepartures = isStayLike ? STAY_ONLY_DEPS : departures;

    // Island hopping is a tab of its own but not a search type: the package /
    // hotel-only state stays as it was underneath.
    const [islandHops, setIslandHops] = useState([]);
    const offerIslandHopping = islandHopping && !specificSearch && !specificAccom;
    useEffect(() => {
        if (offerIslandHopping) {
            PackageDatasource.getIslandHops(baseUrl).then((groups) => setIslandHops(Array.isArray(groups) ? groups : []));
        }
    }, [offerIslandHopping, baseUrl]);
    const hasIslandHops = islandHops.some((g) => g.items && g.items.length > 0);
    const [islandMode, setIslandMode] = useState(false);
    const [selectedHop, setSelectedHop] = useState(null);
    const activeTab = islandMode ? ISLAND_TAB.value : (isVilla ? VILLA_TAB.value : searchType);
    // Island Hopping leaves the search type as it was, so the villa switch follows the tab, not the type
    const villaSwitch = activeTab == VILLA_TAB.value;

    const onTabChange = (tab) => {
        setErrors({});
        if (tab == ISLAND_TAB.value) {
            setIslandMode(true);
            return;
        }
        setIslandMode(false);
        onSearchTypeChange(tab == VILLA_TAB.value ? villaType : tab);
    };

    const goToHop = () => {
        if (!selectedHop) {
            setErrors({ hop: "Choose an island hop" });
            return;
        }
        window.location.href = selectedHop.url;
    };

    const onSearchTypeChange = (type) => {
        if (type == searchType) {
            return;
        }
        setSearchType(type);
        setSavedSearchType(type);
        if (isVillaType(type)) {
            setVillaType(type);
        }
        // Availability differs between the two indexes - the pickers
        // re-validate the destination themselves, but selected dates must be
        // cleared so a stale date cannot be submitted against the new type.
        setSelectedFromDate(null);
        setSelectedToDate(null);
        setSelectedDuration(null);
    };

    useEffect(() => {

        var departureToUse = savedDeparture;
        // The destination cookie may hold a legacy single object or the new
        // array - normalise to an array either way.
        var destinationsToUse = Array.isArray(savedDestination) ? savedDestination : (savedDestination ? [savedDestination] : []);
        var startDateToUse = savedStartDate ? parse(savedStartDate, "yyyy-MM-dd", new Date()) : null;
        var endDateToUse = savedToDate ? parse(savedToDate, "yyyy-MM-dd", new Date()) : null;
        if (specificSearch) {
            departureToUse = specificSearch.dep;
            destinationsToUse = specificSearch.to ? [specificSearch.to] : [];
            startDateToUse = parse(specificSearch.startDate, "yyyy-MM-dd", new Date());
            endDateToUse = parse(specificSearch.endDate, "yyyy-MM-dd", new Date());
            const roomsFromString = deserializeRooms(specificSearch.pax);
            setRooms(roomsFromString);

        } else if (specificAccom) {
            destinationsToUse = [specificAccom];
            setDestinations([specificAccom]);
        }

        const destinationCodes = destinationsToUse.map((d) => d.code).join('-');

        async function fetchData() {
            // A saved month search is a departure-date window, so validate it
            // against the whole range rather than the exact start date.
            var isMonthSearch = !specificSearch && savedDateMode == "month";
            var validateResponse = await dataSource.validate(departureToUse, destinationCodes, startDateToUse, endDateToUse, isMonthSearch);

            if (validateResponse.isValidDeparture) {
                setDepartures(departureToUse);
            }
            // Prefer the API-resolved destination list: it covers multi-code
            // segments (e.g. "AL-GR" -> Albania + Greece) and fills in names an
            // embedded booking URL doesn't carry. Fall back to the saved values.
            if (validateResponse.isValidDestination && !specificAccom) {
                if (validateResponse.destinations && validateResponse.destinations.length > 0) {
                    setDestinations(validateResponse.destinations.map((d) => ({ code: d.code, name: d.name })));
                } else {
                    setDestinations(destinationsToUse);
                }
            }
            if (validateResponse.isValidDates) {
                setSelectedFromDate(startDateToUse);
                setSelectedToDate(endDateToUse)
                if (isMonthSearch && savedDuration) {
                    setSelectedDuration(savedDuration);
                }
            }
        }
        var hasRequiredDeparture = isStayLike || (departureToUse && departureToUse.length > 0);
        if (hasRequiredDeparture && destinationsToUse.length > 0 && startDateToUse && endDateToUse) {
            fetchData();
        }
    }, []);



    // A villa takes one party: rooms kept from another tab merge into one.
    useEffect(() => {
        if (isVilla && rooms.length > 1) {
            setRooms(toSingleParty(rooms));
        }
    }, [isVilla]);

    // Work out which required fields are missing/incomplete, with a message
    // that points at the specific field so the user knows what to fix.
    const validate = () => {
        const found = {};
        if (!isStayLike && (!departures || departures.length == 0)) {
            found.departure = "Choose a departure airport";
        }
        if (!destinations || destinations.length == 0) {
            found.destination = "Choose a destination";
        }
        if (!selectedFromDate) {
            found.dates = dateMode == "month" ? "Choose a month" : "Choose your dates";
        } else if (dateMode == "dates" && !selectedToDate) {
            // The common trap: an outbound date was picked but the popover was
            // closed before choosing a return.
            found.dates = "Choose a return date";
        } else if (dateMode == "month" && !selectedDuration) {
            found.dates = "Choose a trip length";
        }
        return found;
    };

    const submit = () => {
        const found = validate();
        setErrors(found);
        if (Object.keys(found).length > 0) {
            return;
        }

        if (!isStayLike) {
            setSavedDeparture(departures);
        }


        if (!specificAccom) {
            setSavedDestination(destinations);
        }

        const fromDate = format(selectedFromDate, "yyyy-MM-dd");
        const toDate = format(selectedToDate, "yyyy-MM-dd");
        // Persist the user's literal picks so a reload restores what they chose
        // (the widened window below is only for the outgoing booking URL).
        setSavedStartDate(fromDate);
        setSavedToDate(toDate);
        setSavedRooms(rooms);
        // Remember the search type (packages / hotel-only) along with the date
        // mode, duration and ± fuzziness so the whole search restores on return.
        setSavedSearchType(searchType);
        setSavedDateMode(dateMode);
        setSavedDuration(dateMode == "month" ? selectedDuration : null);
        setSavedFlexDays(dateMode == "dates" ? flexDays : 0);

        // The booking engine takes a duration plus a start-date window
        // (earliest..latest start) - see helpers/bookingUrl.js. Earliest start
        // can never be before tomorrow.
        const { nights, earliestStart, latestStart } = computeStartWindow({
            dateMode,
            selectedFromDate,
            selectedToDate,
            selectedDuration,
            flexDays,
            earliestAllowedStart: startOfTomorrow(),
        });
        const roomsString = serializeRooms(rooms);
        // Hyphen-join the selected destination codes into the URL segment.
        const destinationCodes = destinations.map((d) => d.code).join('-');

        window.location.href = buildBookingUrl({
            bookingEngineBaseUrl,
            searchType,
            departures,
            destinationCodes,
            nights,
            earliestStart,
            latestStart,
            roomsString,
        });
    }

    // Clear (or refresh) each shown error as the user corrects a field - but
    // only once errors are already on screen, so we never flag a field before
    // the first submit attempt.
    useEffect(() => {
        setErrors((prev) => {
            if (Object.keys(prev).length === 0) return prev;
            const current = validate();
            const next = {};
            for (const key of Object.keys(prev)) {
                if (current[key]) next[key] = current[key];
            }
            return next;
        });
    }, [departures, destinations, selectedFromDate, selectedToDate, selectedDuration, dateMode, searchType]);

    let portalContent = null;


    window.toggleSearchForm = () => {
        setIsSearchOpen(!isSearchOpen);
    }

    var showSearch = showForm || (isSearchOpen || !isMobile);
    // the comp's order: Holidays, Hotel Only, Island Hopping (when there are hops), Villas
    const visibleTabs = specificAccom || specificSearch
        ? SEARCH_TYPES
        : [...SEARCH_TYPES, ...(hasIslandHops ? [ISLAND_TAB] : []), VILLA_TAB];
    // Both variants are the comp's card and layout; only what sits over a photo differs (white tabs, the 90% white card,
    // its shadow).
    const onImage = variant == "hero";
    const isHero = true;

    // The sliding underline under the active tab (measured, so it follows the
    // label widths and the stacked mobile layout).
    const tabsRef = useRef(null);
    const formPanelRef = useRef(null);
    const [underline, setUnderline] = useState(null);
    useLayoutEffect(() => {
        const measure = () => {
            const active = tabsRef.current?.querySelector('[aria-selected="true"]');
            setUnderline(active ? { left: active.offsetLeft, width: active.offsetWidth } : null);
        };
        measure();
        window.addEventListener("resize", measure);
        document.fonts?.ready.then(measure);
        return () => window.removeEventListener("resize", measure);
    }, [activeTab, visibleTabs.length, showSearch]);

    const tabText = onImage ? "text-white" : "text-ink";
    const panelClass = isHero
        // the comp's card: white at 90% over the photo with a soft shadow, 20px corners on phones (inset to the comp's 384px)
        // and 30px from md; from xl it has the comp's padding (one row, as the 1726px comp - the row needs a ~1280px card).
        // Tablets and small laptops (md to xl, no comp) get a 32px padded card. Its labels, fields and icons are styled by .oly-search-hero (index.css)
        ? "oly-search-hero text-ink max-md:mx-[0.16875rem] max-md:rounded-[1.25rem] max-md:px-[1.58125rem] max-md:pt-[1.26875rem] max-md:pb-[2.375rem] md:rounded-[1.875rem] md:px-8 md:pt-6 md:pb-8 xl:pl-[2.40625rem] xl:pr-[2.1875rem] xl:pt-[1.4375rem] xl:pb-[2.625rem] " +
            (onImage
                ? "bg-white/90 max-md:backdrop-blur-sm max-md:shadow-[0_-0.5rem_2.625rem_1.125rem_rgb(0_0_0/0.1)] md:shadow-[0_-0.75rem_3.9375rem_1.6875rem_rgb(0_0_0/0.1)]"
                : "oly-search-compact bg-white")
        : "rounded-2xl bg-white p-3 shadow-card text-ink border border-sky-100 max-md:p-5";

    // As the mockup: one row of cells whose column widths animate between tabs - a
    // cell that is not used by the tab shrinks to nothing and fades out rather than
    // jumping. Every picker stays mounted, so lists and selections survive a switch.
    const columns = {
        packages: "minmax(0,1fr) minmax(0,1fr) minmax(0,0fr) minmax(0,1fr) minmax(0,1fr) auto",
        stays: "minmax(0,0fr) minmax(0,2fr) minmax(0,0fr) minmax(0,1fr) minmax(0,1fr) auto",
        island: "minmax(0,0fr) minmax(0,0fr) minmax(0,4fr) minmax(0,0fr) minmax(0,0fr) auto",
    }[islandMode ? "island" : (isStayLike ? "stays" : "packages")];
    // the comp's hero spaces the fields 20px apart. The hero keeps the one-row layout for xl and up (the comp's 1280px card);
    // below that (md to xl) its fields sit two to a row, unused ones taken out as on phones, the button the full width.
    const cellPad = isHero ? "xl:px-[0.625rem]" : "md:px-1";
    const cell = (on, extra = "") => "min-w-0 overflow-x-clip transition-[padding,opacity] duration-500 " + extra + " " +
        (on ? "opacity-100 " + cellPad
            : isHero ? "max-xl:hidden opacity-0 pointer-events-none xl:px-0" : "max-md:hidden opacity-0 pointer-events-none md:px-0");
    const cellProps = (on, extra) => ({ className: cell(on, extra), "aria-hidden": !on, inert: !on });
    // tablet hero: a field that is alone on its row takes both columns (Hotel Only's destination, the island hop)
    const wide = isHero ? "md:max-xl:col-span-2" : "";
    const grid = isHero
        ? "grid grid-cols-1 gap-[1.375rem] md:grid-cols-2 md:gap-x-5 md:gap-y-4 xl:gap-0 xl:-mx-[0.625rem] xl:items-end xl:grid-cols-(--cols) xl:transition-[grid-template-columns] xl:duration-500 xl:ease-in-out" + (hasErrors ? " max-xl:gap-y-8" : "")
        : "grid grid-cols-1 gap-4 md:gap-0 md:-mx-1 md:items-end md:grid-cols-(--cols) md:transition-[grid-template-columns] md:duration-500 md:ease-in-out" + (hasErrors ? " max-md:gap-y-8" : "");

    return (
        <IsMobileContext value={isMobile}>
            <DataSourceContext value={dataSource}>
                <div className={showSearch ? "" : "hidden"}>
                    {visibleTabs.length > 1 && (
                        <div ref={tabsRef} role="tablist"
                            className={"relative flex items-center justify-around md:justify-start gap-0 whitespace-nowrap " +
                                (isHero ? "max-md:justify-between max-md:px-[0.5625rem] text-[1.27062rem] font-medium md:[&>[role=tab]:nth-child(3)]:-ml-[0.3125rem] md:pl-8 xl:pl-[7.8125rem] " + (onImage ? "md:text-[1.3125rem] md:gap-[2.3875rem]" : "md:text-base md:gap-[1.875rem]") : "font-semibold text-base md:text-sm md:gap-6 lg:gap-8") + " " + tabText}>
                            {visibleTabs.map((tab) => (
                                <button key={tab.value} type="button" role="tab"
                                    aria-selected={activeTab == tab.value}
                                    onClick={() => onTabChange(tab.value)}
                                    className={"flex flex-col md:flex-row items-center transition-opacity duration-200 cursor-pointer " +
                                        (isHero ? "gap-[0.14375rem] pb-[0.425rem] md:gap-[0.8125rem] md:pb-2 " : "gap-1.5 pb-2 md:gap-2 ") +
                                        (activeTab == tab.value ? "opacity-100" : (onImage ? "opacity-70" : "opacity-100") + " hover:opacity-100")}>
                                    {tab.icon}
                                    {tab.shortLabel
                                        ? <><span className="md:hidden">{tab.shortLabel}</span><span className="max-md:hidden">{tab.label}</span></>
                                        : <span>{tab.label}</span>}
                                </button>
                            ))}
                            {underline && (
                                <span aria-hidden="true" style={{ left: underline.left, width: underline.width }}
                                    className={"pointer-events-none absolute bottom-0 rounded-full transition-[left,width] duration-300 ease-out " + "h-[0.14563rem] md:h-[0.21875rem] " + (onImage ? "bg-white" : "bg-ink")} />
                            )}
                        </div>
                    )}
                    <FormPanelContext value={formPanelRef}>
                    {/* A field's validation message hangs below it (absolutely, so the row doesn't shift): while any is
                        showing, the card grows to hold it - and on phones the stacked fields move apart. */}
                    <div ref={formPanelRef} className={"relative " + (isHero ? "mt-[1.375rem] md:mt-[1.28125rem] " : "mt-4 ") + panelClass + (hasErrors && !isHero ? " md:pb-7" : "")}>
                        {/* The villa switch stays mounted and opens/closes its row (0fr <-> 1fr) at the columns' pace, so
                            the card grows and shrinks smoothly rather than jumping when the Villas tab comes and goes. */}
                        <div aria-hidden={!villaSwitch} inert={!villaSwitch}
                            className={"grid transition-[grid-template-rows,opacity] duration-500 ease-in-out " + (villaSwitch ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 pointer-events-none")}>
                            <div className="min-h-0 overflow-hidden">
                                <div className={isHero ? "pb-5 xl:pb-4" : "pb-4"}>
                                    <div role="radiogroup" aria-label="Villa search" className={"inline-flex rounded-full bg-sky-50 p-1 font-semibold " + (isHero ? "text-base xl:text-sm" : "text-sm")}>
                                        {VILLA_TYPES.map((type) => (
                                            <button key={type.value} type="button" role="radio" aria-checked={searchType == type.value}
                                                onClick={() => { setErrors({}); onSearchTypeChange(type.value); }}
                                                className={"rounded-full px-4 py-1.5 transition-colors duration-200 cursor-pointer " +
                                                    (searchType == type.value ? "bg-navy-700 text-white" : "text-ink hover:bg-sky-100")}>
                                                {type.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ "--cols": columns }} className={grid}>
                            <div {...cellProps(!islandMode && !isStayLike)}>
                                <DataSourceContext value={packagesDataSource}>
                                    <DeparturePicker
                                        selectedAirports={departures}
                                        updateOverviewText={setOverViewDepartureText}
                                        onUpdate={setDepartures}
                                        error={errors.departure}
                                    />
                                </DataSourceContext>
                            </div>
                            <div {...cellProps(!islandMode, isStayLike ? wide : "")}>
                                <DestinationPicker
                                    departureAirports={effectiveDepartures}

                                    accomIsLocked={!!specificAccom}
                                    selectedDestinations={destinations}
                                    onUpdate={setDestinations}
                                    error={errors.destination}
                                />
                            </div>
                            <div {...cellProps(islandMode, wide)}>
                                {hasIslandHops && (
                                    <IslandHopPicker groups={islandHops} selected={selectedHop} error={errors.hop}
                                        onSelect={(hop) => { setSelectedHop(hop); setErrors({}); }} />
                                )}
                            </div>
                            <div {...cellProps(!islandMode)}>
                                <DatePicker
                                    departureAirports={effectiveDepartures}
                                    destination={destinations.map((d) => d.code).join('-')}
                                    error={errors.dates}

                                    selectedFrom={selectedFromDate}
                                    selectedTo={selectedToDate}
                                    onChange={(from, to) => {
                                        setSelectedFromDate(from);
                                        setSelectedToDate(to);
                                    }}
                                    dateMode={dateMode}
                                    onDateModeChange={setDateMode}
                                    selectedDuration={selectedDuration}
                                    onDurationChange={setSelectedDuration}
                                    flexDays={flexDays}
                                    onFlexDaysChange={setFlexDays}
                                />
                            </div>
                            <div {...cellProps(!islandMode)}>
                                <PaxPicker
                                    rooms={rooms}
                                    onChange={setRooms}
                                    singleParty={isVilla}
                                />
                            </div>
                            <div className={"flex items-end " + (isHero ? "max-md:pt-[0.23125rem] md:max-xl:pt-1 " + wide + " " : "max-md:pt-2 ") + cellPad}>
                                <button onClick={() => islandMode ? goToHop() : submit()} type="submit"
                                    className={"oly-btn oly-btn-primary w-full whitespace-nowrap " +
                                        (isHero ? "bg-gradient-yellow text-ink font-bold h-[3.89562rem] rounded-[0.625rem] text-[1.125rem] md:rounded-[0.3125rem]" + (onImage ? "md:h-[2.875rem] md:text-lg xl:h-[2.8125rem] xl:w-[9.84375rem] xl:text-base" : "md:h-[2.625rem] md:text-[0.9375rem] xl:w-[8.75rem]") : "max-md:py-4 max-md:text-lg md:w-auto md:min-w-32 lg:min-w-36")}>
                                    {islandMode ? "View Island Hop" : "Search Now"}
                                </button>
                            </div>
                        </div>
                    </div>
                    </FormPanelContext>
                </div>
                {portalContent}
            </DataSourceContext>
        </IsMobileContext>);
};
export default OlympicSearch;
